import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";

interface StageCue {
  /** Cast slot: 0 lead, 1 co-lead, 2 supporting. */
  speaker: 0 | 1 | 2;
  line: string;
  holdMs: number;
}

interface StageScene {
  title: string;
  cues: readonly StageCue[];
}

/** Popular Shakespeare scenes - classic shape, dogdell dog cameos. */
const SHAKESPEARE_SCENES: readonly StageScene[] = [
  {
    title: "Romeo and Juliet",
    cues: [
      { speaker: 0, line: "But soft, what light through yonder window breaks?", holdMs: 4200 },
      { speaker: 0, line: "It is the east, and Juliet is the sun… also, possibly a golden retriever.", holdMs: 4000 },
      { speaker: 1, line: "O Romeo, Romeo! wherefore art thou Romeo? And why is there mud on your paws?", holdMs: 4200 },
      { speaker: 1, line: "What's in a name? That which we call a rose by any other name would smell as sweet… unless you rolled in it.", holdMs: 4800 },
      { speaker: 0, line: "Call me but love, and I'll be new baptized. Also please hide the chew toy.", holdMs: 3800 },
      { speaker: 1, line: "Good night, good night! Parting is such sweet sorrow… see you at the treat cart.", holdMs: 4200 },
    ],
  },
  {
    title: "Hamlet",
    cues: [
      { speaker: 0, line: "To be, or not to be, that is the question.", holdMs: 4200 },
      { speaker: 0, line: "Whether 'tis nobler in the mind to suffer… or dig a very important hole.", holdMs: 4400 },
      { speaker: 2, line: "These are but wild and whirling words, my lord. Also: sit. Stay. Optional.", holdMs: 3800 },
      { speaker: 0, line: "Alas, poor Yorick! I knew him, Horatio… good boy. Excellent fetch.", holdMs: 4200 },
      { speaker: 0, line: "A fellow of infinite jest, of most excellent fancy… and one slightly chewed tennis ball.", holdMs: 4200 },
      { speaker: 2, line: "Good night, sweet prince, and flights of angels sing thee to thy rest. Wag softly.", holdMs: 4500 },
    ],
  },
  {
    title: "Julius Caesar",
    cues: [
      { speaker: 0, line: "Friends, Romans, countrymen, lend me your ears… and maybe a spare biscuit.", holdMs: 4200 },
      { speaker: 0, line: "I come to bury Caesar, not to praise him. Also not to dig him up later. Probably.", holdMs: 4000 },
      { speaker: 2, line: "Not that I loved Caesar less, but that I loved Rome more… and the park pond.", holdMs: 4200 },
      { speaker: 1, line: "Et tu, Brute? Then fall, Caesar! (dramatic belly flop)", holdMs: 3800 },
      { speaker: 0, line: "This was the most unkindest cut of all… worse than a bath.", holdMs: 4000 },
    ],
  },
  {
    title: "Macbeth",
    cues: [
      { speaker: 0, line: "Is this a dagger which I see before me, the handle toward my hand… or a squeaky bone?", holdMs: 4500 },
      { speaker: 0, line: "Come, let me clutch thee. I have thee not, and yet I see thee still. Typical.", holdMs: 4200 },
      { speaker: 1, line: "Out, damned spot! out, I say! The mud never leaves the white fur.", holdMs: 3800 },
      { speaker: 2, line: "Double, double toil and trouble; fire burn, and cauldron bubble… then nap.", holdMs: 4400 },
      { speaker: 0, line: "Life's but a walking shadow, a poor player… that struts, frets, then chases its own tail.", holdMs: 5000 },
    ],
  },
  {
    title: "A Midsummer Night's Dream",
    cues: [
      { speaker: 2, line: "I'll put a girdle round about the earth in forty minutes… if the leash allows.", holdMs: 4000 },
      { speaker: 2, line: "Lord, what fools these mortals be! Especially around tennis balls.", holdMs: 3600 },
      { speaker: 0, line: "I have had a most rare vision… a dream of infinite belly rubs.", holdMs: 4200 },
      { speaker: 1, line: "Methinks I see these things with parted eye… when everything seems double. Treats included.", holdMs: 4400 },
      { speaker: 2, line: "If we shadows have offended, think but this, and all is mended… with snacks.", holdMs: 4400 },
    ],
  },
  {
    title: "Henry V",
    cues: [
      { speaker: 0, line: "Once more unto the breach, dear friends, once more;", holdMs: 4000 },
      { speaker: 0, line: "Or close the wall up with our English dead… metaphorically. No actual digging.", holdMs: 4000 },
      { speaker: 0, line: "We few, we happy few, we band of brothers… and good dogs.", holdMs: 4000 },
      { speaker: 2, line: "For he to-day that sheds his blood with me shall be my brother… and share the water dish.", holdMs: 4400 },
    ],
  },
  {
    title: "As You Like It",
    cues: [
      { speaker: 0, line: "All the world's a stage,", holdMs: 3200 },
      { speaker: 0, line: "And all the men and women merely players… the dogs have better reviews.", holdMs: 4000 },
      { speaker: 0, line: "They have their exits and their entrances… and their zoomies.", holdMs: 3800 },
      { speaker: 1, line: "And one man in his time plays many parts… sit, shake, roll over, encore.", holdMs: 4000 },
    ],
  },
  {
    title: "Twelfth Night",
    cues: [
      { speaker: 0, line: "If music be the food of love, play on;", holdMs: 3800 },
      { speaker: 0, line: "Give me excess of it… and also bacon. Surfeiting optional.", holdMs: 4000 },
      { speaker: 1, line: "If this be so, as yet the glass seems true… I shall have share in this most happy wreck. And the crumbs.", holdMs: 4400 },
      { speaker: 2, line: "Some are born great, some achieve greatness, and some have greatness thrust upon 'em… like a cone.", holdMs: 4600 },
    ],
  },
];

export interface StageActorSpot {
  x: number;
  z: number;
  bubbleY: number;
}

/**
 * Amphitheater troupe: one shared Shakespeare-ish script with dogdell dog jokes.
 */
export class ParkStageShow {
  readonly root = new THREE.Group();
  private readonly actors: {
    el: HTMLDivElement;
    object: CSS2DObject;
    x: number;
    z: number;
  }[] = [];
  private sceneIndex = Math.floor(Math.random() * SHAKESPEARE_SCENES.length);
  private cueIndex = -1;
  private hideAt = 0;
  private nextAt = 0;
  private announcing = false;

  constructor(spots: StageActorSpot[]) {
    for (const spot of spots.slice(0, 3)) {
      const el = document.createElement("div");
      el.className = "world-label world-bubble world-bubble-sitter world-bubble-stage";
      el.hidden = true;
      const object = new CSS2DObject(el);
      object.position.set(spot.x, spot.bubbleY, spot.z);
      object.visible = false;
      this.root.add(object);
      this.actors.push({ el, object, x: spot.x, z: spot.z });
    }
    this.nextAt = performance.now() + 2200;
  }

  update(now: number, focusX: number, focusZ: number): void {
    if (this.actors.length === 0) {
      return;
    }
    const center = this.actors[1] ?? this.actors[0]!;
    const near = Math.hypot(center.x - focusX, center.z - focusZ) < 28;

    if (this.hideAt > 0 && now >= this.hideAt) {
      this.clearBubbles();
      this.hideAt = 0;
      if (this.announcing) {
        this.announcing = false;
        this.cueIndex = 0;
        this.nextAt = now + 500;
      } else {
        this.cueIndex += 1;
        const scene = SHAKESPEARE_SCENES[this.sceneIndex]!;
        if (this.cueIndex >= scene.cues.length) {
          this.sceneIndex = (this.sceneIndex + 1) % SHAKESPEARE_SCENES.length;
          this.cueIndex = -1;
          this.nextAt = now + 2800 + Math.random() * 1800;
        } else {
          this.nextAt = now + 350;
        }
      }
      return;
    }

    if (this.hideAt > 0 || now < this.nextAt) {
      return;
    }

    if (!near) {
      this.nextAt = now + 3500 + Math.random() * 2500;
      return;
    }

    if (this.cueIndex < 0) {
      this.announceScene(now);
      return;
    }

    this.speakCue(now);
  }

  destroy(): void {
    this.clearBubbles();
    for (const actor of this.actors) {
      actor.el.remove();
      actor.object.removeFromParent();
    }
    this.actors.length = 0;
    this.root.removeFromParent();
  }

  private announceScene(now: number): void {
    const scene = SHAKESPEARE_SCENES[this.sceneIndex]!;
    const host = this.actors[1] ?? this.actors[0]!;
    this.clearBubbles();
    host.el.textContent = `${scene.title}`;
    host.el.hidden = false;
    host.object.visible = true;
    this.announcing = true;
    this.hideAt = now + 2400;
  }

  private speakCue(now: number): void {
    const scene = SHAKESPEARE_SCENES[this.sceneIndex]!;
    const cue = scene.cues[this.cueIndex];
    if (!cue) {
      this.cueIndex = -1;
      this.nextAt = now + 2000;
      return;
    }
    const actor = this.actors[cue.speaker] ?? this.actors[0]!;
    this.clearBubbles();
    actor.el.textContent = cue.line;
    actor.el.hidden = false;
    actor.object.visible = true;
    this.hideAt = now + cue.holdMs;
  }

  private clearBubbles(): void {
    for (const actor of this.actors) {
      actor.el.hidden = true;
      actor.el.textContent = "";
      actor.object.visible = false;
    }
  }
}
